/**
 * React dependencies
 */
import { useState, useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

/**
 * External dependencies
 */
import _get from 'lodash.get';

/**
 * Local dependencies
 */
import validation from 'utils/validation';
import Textfield from 'components/Textfield';
import { submitFormData, getVenues } from 'utils/axios';
import Selectfield from 'components/Select';
import HiddenTextfield from 'components/HiddenTextfield';
import Checkbox from 'components/Checkbox';
import Button from 'components/Button';
import { ROUTE_RESULT } from 'constants/routes';
import { SEVERITY_ERROR } from 'constants/snackbar';
import { apiActions } from 'store/slices/api';
import { snackbarActions } from 'store/slices/snackbar';

/**
 * Data capture form page
 * Shows a form that user has to fill in to enter the prize draw.
 */
const DataCapture = () => {
  const { venue } = useSelector(state => state.api);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const search = useLocation().search;
  const [loading, setLoading] = useState(false);
  const [venues, setVenues] = useState([]);
  const { handleSubmit, control } = useForm();

  // Compass UK&I and The Jockey Club privacy policy links are hard coded
  const tjcPrivacy = "https://www.thejockeyclub.co.uk/privacy-policy/";
  const compassPrivacy = "https://www.compass-group.co.uk/privacy-notice/";

  // Only show location dropdown to visitors who haven't accessed ?venue=
  const needsVenueSelection = ! venue;

  // Fetch the venue list to populate the dropdown.
  useEffect(() => {
    if ( ! needsVenueSelection ) return;

    let active = true;

    getVenues()
      .then( response => {
        if ( ! active ) return;
        setVenues( _get( response, 'data.data', [] ) );
      })
      .catch( () => {
        if ( ! active ) return;
        dispatch(snackbarActions.setSeverity({
          severity: SEVERITY_ERROR
        }));
        dispatch(snackbarActions.setMessage({
          message: 'Could not load the list of locations.'
        }));
        dispatch(snackbarActions.showSnackBar(true));
      });

    return () => { active = false; };
  }, [dispatch, needsVenueSelection]);

  const selectedVenueId = useWatch({ control, name: 'venue', defaultValue: '' });
  const selectedVenue = venues.find( v => String( v.venue_id ) === String( selectedVenueId ) );

  const venueOptions = venues.map( v => ({
    value: String( v.venue_id ),
    label: v.name,
  }) );

  const activeVenueType = needsVenueSelection
    ? _get( selectedVenue, 'venue_type', '' )
    : _get( venue, 'venue_type', '' );

  /**
   * Data capture form submit handler.
   * Sends the form data to the api to submit
   * the entry for the prize draw.
   * 
   * @param {Object} data - the submitted form data
   */
  const onSubmit = data => {
    setLoading(true);

    const chosenVenue = venues.find( v => String( v.venue_id ) === String( data.venue ) );
    const payload = {
      ...data,
      'venue-name': needsVenueSelection
        ? _get( chosenVenue, 'name', '' )
        : _get( venue, 'venue_name', '' ),
    };

    submitFormData( payload )
      .then( response => {
        const entryResult = _get( response, 'data.data.entry_result', 0 );
        const prize       = _get( response, 'data.data.prize.description', null );

        dispatch(apiActions.setSubmission({
          userWon: entryResult === 1 ? true : false,
          prize: !!prize ? prize : null
        }));
        navigate(`${ROUTE_RESULT}${search}`);
      })
      .catch( e => {
        setLoading(false);
        dispatch(snackbarActions.setSeverity({
          severity: SEVERITY_ERROR
        }));
        dispatch(snackbarActions.setMessage({
          message: 'Server connection error.'
        }));
        dispatch(snackbarActions.showSnackBar(true));
      });
  }

  return (
    <section className="DataCapture">
      <h3 className="h3">Fill in your details</h3>
      <p>If you're a lucky winner we'll email your voucher to you.</p>

      <form noValidate autoComplete='off' onSubmit={handleSubmit(onSubmit)} className="DataCapture__form">
        { needsVenueSelection ? (
          <Selectfield
            id='venue'
            label='Which location are you at?'
            name='venue'
            control={control}
            validation={validation.venueName}
            options={venueOptions}
          />
        ) : (
          <HiddenTextfield
            control={control}
            name='venue'
            id='venue'
            defaultValue={String( venue.id )}
          />
        ) }
        <div className={['DataCapture__row', needsVenueSelection ? 'DataCapture__row--split' : ''].join(' ').trim()}>
          <Textfield
            id='given-name'
            label='First name'
            type='text'
            name='first-name'
            control={control}
            validation={validation.firstName}
          />
          <Textfield
            id='family-name'
            label='Last name'
            type='text'
            name='last-name'
            control={control}
            validation={validation.lastName}
          />
        </div>
        <Textfield
          id='email'
          label='Email address'
          type='email'
          name='email'
          control={control}
          validation={validation.email}
        />

        <div className="DataCapture__privacy">
          <p>By entering the competition you agree to receive the latest news, offers and announcements from your selected Jockey Club Venue and Levy Conference & Events team, part of Compass Group UK & Ireland.</p>
          <p>We will manage your data in accordance with <a target="_blank" rel="noreferrer" href={tjcPrivacy}>The Jockey Club privacy policy</a> and <a target="_blank" rel="noreferrer" href={compassPrivacy}>Compass UK&I privacy policy</a>. You can unsubscribe at any time by emailing <a href="mailto:marketing@limevenueportfolio.com">marketing@limevenueportfolio.com</a>.</p>
        </div>
  
        <HiddenTextfield
          control={control}
          name='marketing'
          id='marketing'
          validation={validation.marketing}
          defaultValue='true'
        />

        {Number(activeVenueType) === 1 && (
          <HiddenTextfield
            control={control}
            name='club_specific_marketing'
            id='club_specific_marketing'
            defaultValue='true'
          />
        )}
        <Textfield
          control={control}
          name='website'
          id='website'
          className="butwhy"
        />
        <Button
          loading={loading ? loading : undefined}
          variant='contained'
          type='submit'
          className="mx-auto"
        >
          Submit
        </Button>
      </form>
    </section>
  );
};

export default DataCapture;